// src/components/Shared/RecipeCards/RecipeCardWide.tsx
// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { getAvatarURL } from '../../../utils/avatar.ts';
import RecipeOptionsMenu from '../../RecipeOptionsMenu.tsx';
import SplitActionButton from '../../ui/atoms/Button/SplitActionButton.tsx';
import RecipeTagList from '../../ui/molecules/RecipeTagList.tsx';
import { WarningBadge } from '../../ui/atoms/Warning/WarningBadge.tsx';
import { BORDER_COLORS, BACKGROUND_COLORS } from '../../ui/atoms/Warning/WarningBadge.tsx';
import type { WarningType } from '../../ui/atoms/Warning/WarningBadge.tsx';

interface RecipeCardWideProps {
  recipe: any;
  showOptions?: boolean;
  onAddToPlan: () => void;
  hideAddButton?: boolean;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
  /** if true, show the warning badge */
  hasWarning?: boolean;
  /** whether "avoid" (orange) or "allergy" (red) */
  warningType?: WarningType;
  /** tapped badge → show sheet */
  onWarningClick?: () => void;
}

const RecipeCardWide: React.FC<RecipeCardWideProps> = ({
  recipe,
  showOptions = false,
  onAddToPlan,
  hideAddButton = false,
  isBookmarked,
  onToggleBookmark,
  hasWarning = false,
  warningType = 'avoid',
  onWarningClick,
}) => {
  const creator = recipe.user;
  const fallbackRecipeImage = '/no-image.png';

  // use explicit border colors so Tailwind doesn't purge them
  const warningBorderClass = hasWarning ? BORDER_COLORS[warningType] : 'border-gray-300';
  const warningBgClass = hasWarning ? BACKGROUND_COLORS[warningType] : 'bg-white';
  const cardBorderClass = `border ${warningBorderClass} ${warningBgClass}`;

  return (
    <div className="relative">
      {hasWarning && onWarningClick && (
        <div className="absolute top-2 right-2">
          <WarningBadge type={warningType} onClick={onWarningClick} />
        </div>
      )}

      <div className="absolute bottom-0 right-0 z-10">
        {!hideAddButton && (
          <SplitActionButton
            variant="search"
            isBookmarked={isBookmarked}
            onToggleBookmark={onToggleBookmark}
            onAddToMealPlan={onAddToPlan}
          />
        )}
      </div>

      <Link to={`/app/recipe/${recipe.id}`} className="block">
        <div className={`rounded-xl ${cardBorderClass} hover:bg-gray-50 transition overflow-hidden w-full md:max-w-lg mx-auto flex`} data-warning={hasWarning ? warningType : undefined}>
          {/* IMAGE */}
          <div className="w-24 h-24 flex-shrink-0 bg-gray-200 rounded-l overflow-hidden">
            {recipe.imageUrl ? (
              <img
                src={recipe.imageUrl}
                alt={recipe.title}
                className="object-cover w-full h-full"
                onError={(e) => {
                  const t = e.currentTarget;
                  t.onerror = null;
                  t.src = fallbackRecipeImage;
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">
                No Image
              </div>
            )}
          </div>

          {/* CONTENT */}
          <div className="p-2 flex flex-1 flex-col justify-between min-w-0">
            <div>
              <h3 className="font-heading font-medium truncate">
                {recipe.title}
              </h3>
              <div className="flex items-center text-sm text-gray-600 mt-1">
                <img
                  src={getAvatarURL(creator)}
                  alt={creator?.name || creator?.username || 'User'}
                  className="w-4 h-4 rounded-full mr-2"
                />
                <span className="truncate">
                  {creator?.name || creator?.username || 'Unknown'}
                </span>
              </div>
            </div>

            <div className="flex items-center mt-2">
              <div className="relative flex-1 min-w-0">
                <div
                  className="flex items-center gap-1 overflow-hidden whitespace-nowrap min-w-0"
                  style={{
                    WebkitMaskImage:
                      "linear-gradient(to left, transparent, black 20%, black 100%)",
                    maskImage:
                      "linear-gradient(to left, transparent, black 20%, black 100%)",
                    maskRepeat: "no-repeat",
                    maskSize: "200% 100%",
                  }}
                >
                  <RecipeTagList recipeTags={recipe.recipeTags} context="search" />
                </div>

              </div>
            </div>
          </div>
        </div>
      </Link>

      {showOptions && (
        <div className="absolute bottom-2 right-2">
          <RecipeOptionsMenu recipeId={recipe.id} />
        </div>
      )}
    </div>
  );
};

export default RecipeCardWide;
